/**
 * Calendar anchoring for the schedule.
 *
 * The scheduler works in relative project days where day 0 is a Monday and positions
 * 5-6 of every 7-day block are the weekend (see cpm.ts). The calendar start date a user
 * enters is the first day of planned work — not necessarily a Monday, since work can
 * start on any day of the week — so day 0 is the Monday of *that date's own week*, and
 * `projectStartDay` records how far into that week the entered date actually falls (0
 * for a Monday start, 2 for a Wednesday one, and so on). The board still always shows
 * the whole start week, including whatever days sit before the entered date, so a
 * Wednesday start still shows Monday and Tuesday — just with nothing scheduled on them
 * and, in the UI, a clear marker on the day that's the actual start.
 *
 * All dates here are handled as *local* civil dates ("YYYY-MM-DD", no time, no zone).
 * Day arithmetic goes through the Date constructor rather than millisecond offsets so
 * that a DST transition inside a project can't shift the grid by an hour and round wrong.
 */

/** Parse "YYYY-MM-DD" as a local civil date. Returns null for anything malformed. */
export function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  // Rejects impossible dates that the constructor would silently roll over (e.g. 2026-02-30).
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
  return date;
}

/** Formats a civil "YYYY-MM-DD" date for display (e.g. "13 Sep 2026"), or "" for null/unparseable. */
export function formatCivilDate(iso: string | null): string {
  const d = parseIsoDate(iso);
  return d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";
}

export function toIsoDate(d: Date): string {
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mo}-${day}`;
}

/** Today as a local civil date string. */
export function todayIso(): string {
  return toIsoDate(new Date());
}

/** Whole days from `a` to `b`, ignoring clock time and DST. */
function daysBetween(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86_400_000);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** The Monday on or before `d`. This is the board's project day 0. */
export function mondayOfWeek(d: Date): Date {
  // getDay(): 0=Sun … 6=Sat. Monday-start position is 0=Mon … 6=Sun.
  const pos = (d.getDay() + 6) % 7;
  return addDays(d, -pos);
}

/**
 * Project day 0 as a calendar date — the Monday of the entered start date's own week.
 * Null when no start date is set, which is the app's "no calendar" mode.
 */
export function boardStartDate(startIso: string | null): Date | null {
  const start = parseIsoDate(startIso);
  return start ? mondayOfWeek(start) : null;
}

/** The project day a calendar date falls on. Can be negative (before the start week). */
export function projectDayOfDate(startIso: string | null, date: Date): number | null {
  const origin = boardStartDate(startIso);
  return origin ? daysBetween(origin, date) : null;
}

/** The calendar date of a project day. */
export function dateOfProjectDay(startIso: string | null, day: number): Date | null {
  const origin = boardStartDate(startIso);
  return origin ? addDays(origin, day) : null;
}

/** The project day the entered start date itself falls on — 0 for a Monday start, 2 for
 *  a Wednesday one, up to 6 for a Sunday start. Everything before it in week 1 is still
 *  a displayed day (the whole start week always shows), just with no work of its own —
 *  callers that need to flag "this is the actual start" compare a day against this,
 *  never against day 0. */
export function projectStartDay(startIso: string | null): number | null {
  const start = parseIsoDate(startIso);
  if (!start) return null;
  return (start.getDay() + 6) % 7;
}

/** Whether `day` is the entered start date itself — for marking it distinctly in the
 *  UI, since the whole start week is shown regardless of which weekday work begins on. */
export function isProjectStartDay(startIso: string | null, day: number): boolean {
  return projectStartDay(startIso) === day;
}

/** Today as a project day. Negative before the project starts, null with no start date. */
export function todayProjectDay(startIso: string | null, today: string = todayIso()): number | null {
  const date = parseIsoDate(today);
  if (!date) return null;
  return projectDayOfDate(startIso, date);
}

/**
 * The earliest project day unfinished work may be scheduled on: the later of the
 * entered start date and today. Null with no start date, which leaves the schedule
 * exactly as it behaved before calendars existed — nothing is floored, nothing slides.
 *
 * Flooring at the start date (not just at 0) is what keeps work off the lead-in days
 * the start week shows before it — a Wednesday start still displays Monday and Tuesday
 * for context, but nothing can land there.
 */
export function scheduleFloorDay(startIso: string | null, today: string = todayIso()): number | null {
  const startDay = projectStartDay(startIso);
  if (startDay == null) return null;
  const todayDay = todayProjectDay(startIso, today);
  return todayDay == null ? startDay : Math.max(startDay, todayDay);
}

/** Positions 5 and 6 of every 7-day block are the weekend — the same rule cpm.ts schedules by. */
export function isWeekendDay(day: number): boolean {
  return (((day % 7) + 7) % 7) >= 5;
}

/** 1-based week number a project day falls in. */
export function weekOfProjectDay(day: number): number {
  return Math.floor(day / 7) + 1;
}

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Weekday name for a project day — available with or without a calendar, since the
 *  relative week is Monday-start either way. */
export function weekdayNameOfProjectDay(day: number): string {
  return WEEKDAY_SHORT[((day % 7) + 7) % 7];
}

/** "Wed 16 Sep" for a project day, or null with no start date. */
export function formatProjectDay(startIso: string | null, day: number): string | null {
  const d = dateOfProjectDay(startIso, day);
  if (!d) return null;
  return `${WEEKDAY_SHORT[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}

/** Compact "16 Sep" for a project day — no weekday, for tight spaces like an axis. Null with no start date. */
export function axisDateLabel(startIso: string | null, day: number): string | null {
  const d = dateOfProjectDay(startIso, day);
  if (!d) return null;
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}

/**
 * "Wed 16 Sep" once a calendar is set, and a plain "Day 3" before that — for anywhere
 * a bare project-day number would mean nothing to someone standing on site.
 * `dayWord` lets a mid-sentence caller (a toast) lowercase it.
 */
export function projectDayLabel(startIso: string | null, day: number, dayWord: "Day" | "day" = "Day"): string {
  return formatProjectDay(startIso, day) ?? `${dayWord} ${day}`;
}

/** "Mon 14 Sep – Sun 20 Sep" for a run of days, falling back to "Day 7–13" with no calendar. */
export function projectDayRangeLabel(startIso: string | null, days: number[]): string {
  if (!days.length) return "";
  const first = days[0];
  const last = days[days.length - 1];
  const from = formatProjectDay(startIso, first);
  const to = formatProjectDay(startIso, last);
  return from && to ? `${from} – ${to}` : `Day ${first}–${last}`;
}
